import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Cpu, Loader2, Check, Globe, Mail, CreditCard, SlidersHorizontal, Send, Info } from "lucide-react";

const PROVIDERS = [["emergent", "Défaut"], ["openai", "OpenAI"], ["anthropic", "Anthropic"], ["gemini", "Gemini"]];
const PLACEHOLDER = { openai: "gpt-4o", anthropic: "claude-sonnet-4-6", gemini: "gemini-2-flash", emergent: "gpt-4o (défaut)" };

export default function AdminSettings() {
  /* ─────────────── Moteur IA ─────────────── */
  const [llm, setLlm] = useState({ llm_provider: "emergent", llm_model: "", llm_api_key: "" });
  const [llmKeySet, setLlmKeySet] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [testingLlm, setTestingLlm] = useState(false);
  const [llmTest, setLlmTest] = useState(null);

  const loadLlm = () => api.get("/admin/llm").then((r) => {
    setLlm({ llm_provider: r.data.llm_provider || "emergent", llm_model: r.data.llm_model || "", llm_api_key: "" });
    setLlmKeySet(!!r.data.llm_key_set);
  }).catch(() => {});

  const saveLlm = async () => {
    setSavingLlm(true);
    try {
      const payload = { llm_provider: llm.llm_provider, llm_model: llm.llm_model || "" };
      if (llm.llm_provider !== "emergent" && llm.llm_api_key) payload.llm_api_key = llm.llm_api_key;
      await api.put("/admin/llm", payload);
      await loadLlm();
      setLlm((f) => ({ ...f, llm_api_key: "" }));
      toast.success("Moteur IA global enregistré");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSavingLlm(false); }
  };

  const runLlmTest = async () => {
    setTestingLlm(true); setLlmTest(null);
    try {
      const { data } = await api.post("/admin/llm-test", {});
      setLlmTest(data);
      data.ok ? toast.success(`Connexion IA OK (${data.provider} · ${data.latency_ms} ms)`) : toast.error("Échec de la connexion IA");
    } catch (e) { setLlmTest({ ok: false, error: formatAxiosError(e) }); toast.error("Échec du test IA"); }
    finally { setTestingLlm(false); }
  };

  /* ─────────────── Paramètres plateforme (SMTP central + Général) ─────────────── */
  const [pf, setPf] = useState({
    central_email_provider: "smtp",
    smtp_central_enabled: false,
    smtp_central_host: "",
    smtp_central_port: 587,
    smtp_central_user: "",
    smtp_central_password: "",
    smtp_central_from_domain: "",
    postal_url: "",
    postal_api_key: "",
    postal_from_domain: "",
    postal_spf_host: "",
    postal_return_path_host: "",
    postal_return_path_prefix: "psrp",
    platform_app_url: "",
    platform_support_email: "",
    platform_slack_webhook: "",
    platform_trial_days: 14,
  });
  const [smtpPwSet, setSmtpPwSet] = useState(false);
  const [postalKeySet, setPostalKeySet] = useState(false);
  const [slackSet, setSlackSet] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTest, setSmtpTest] = useState(null);

  const loadPlatform = () => api.get("/admin/platform-settings").then(({ data }) => {
    setPf((f) => ({
      ...f,
      // Choix explicite "postal" prioritaire ; sinon, si Postal est configuré
      // (URL ou clé posée), on le garde sélectionné plutôt que retomber sur SMTP.
      central_email_provider:
        data.central_email_provider === "postal" ? "postal"
          : (data.postal_url || data.postal_api_key_set) ? "postal"
            : "smtp",
      smtp_central_enabled: !!data.smtp_central_enabled,
      smtp_central_host: data.smtp_central_host || "",
      smtp_central_port: data.smtp_central_port ?? 587,
      smtp_central_user: data.smtp_central_user || "",
      smtp_central_password: "",
      smtp_central_from_domain: data.smtp_central_from_domain || "",
      postal_url: data.postal_url || "",
      postal_api_key: "",
      postal_from_domain: data.postal_from_domain || "",
      postal_spf_host: data.postal_spf_host || "",
      postal_return_path_host: data.postal_return_path_host || "",
      postal_return_path_prefix: data.postal_return_path_prefix || "psrp",
      platform_app_url: data.platform_app_url || "",
      platform_support_email: data.platform_support_email || "",
      platform_slack_webhook: "",
      platform_trial_days: data.platform_trial_days ?? 14,
    }));
    setSmtpPwSet(!!data.smtp_central_password_set);
    setPostalKeySet(!!data.postal_api_key_set);
    setSlackSet(!!data.platform_slack_webhook_set);
  }).catch(() => {});

  const pfField = (k, v) => setPf((f) => ({ ...f, [k]: v }));

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const payload = {
        central_email_provider: pf.central_email_provider === "postal" ? "postal" : "smtp",
        smtp_central_enabled: !!pf.smtp_central_enabled,
        smtp_central_host: pf.smtp_central_host,
        smtp_central_port: Number(pf.smtp_central_port) || 587,
        smtp_central_user: pf.smtp_central_user,
        smtp_central_from_domain: pf.smtp_central_from_domain,
        postal_url: pf.postal_url,
        postal_from_domain: pf.postal_from_domain,
        postal_spf_host: pf.postal_spf_host,
        postal_return_path_host: pf.postal_return_path_host,
        postal_return_path_prefix: pf.postal_return_path_prefix,
      };
      if (pf.smtp_central_password) payload.smtp_central_password = pf.smtp_central_password;
      if (pf.postal_api_key) payload.postal_api_key = pf.postal_api_key;
      await api.put("/admin/platform-settings", payload);
      await loadPlatform();
      toast.success("Envoi central enregistré");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSavingSmtp(false); }
  };

  const saveGeneral = async () => {
    setSavingGeneral(true);
    try {
      const payload = {
        platform_app_url: pf.platform_app_url,
        platform_support_email: pf.platform_support_email,
        platform_trial_days: Number(pf.platform_trial_days) || 0,
      };
      if (pf.platform_slack_webhook) payload.platform_slack_webhook = pf.platform_slack_webhook;
      await api.put("/admin/platform-settings", payload);
      await loadPlatform();
      toast.success("Paramètres généraux enregistrés");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSavingGeneral(false); }
  };

  const runSmtpTest = async () => {
    if (!testTo.trim()) return toast.error("Adresse de destination requise");
    setSmtpTesting(true); setSmtpTest(null);
    try {
      const { data } = await api.post("/admin/smtp-central-test", { to: testTo.trim() });
      setSmtpTest(data);
      data.ok ? toast.success("Email de test envoyé") : toast.error("Échec de l'envoi de test");
    } catch (e) { setSmtpTest({ ok: false, error: formatAxiosError(e) }); toast.error("Échec du test SMTP"); }
    finally { setSmtpTesting(false); }
  };

  /* ─────────────── Stripe plateforme ─────────────── */
  const [stripe, setStripe] = useState({
    stripe_price_pro_monthly: "",
    stripe_price_pro_yearly: "",
    stripe_price_business_monthly: "",
    stripe_price_business_yearly: "",
    stripe_price_enterprise_monthly: "",
    stripe_price_enterprise_yearly: "",
    stripe_billing_webhook_secret: "",
  });
  const [stripeKeySet, setStripeKeySet] = useState(false);
  const [webhookSecretSet, setWebhookSecretSet] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);

  const loadStripe = () => api.get("/admin/stripe").then(({ data }) => {
    setStripeKeySet(!!data.stripe_api_key_set);
    setWebhookSecretSet(!!data.stripe_billing_webhook_secret_set);
    setStripe((f) => ({
      ...f,
      stripe_price_pro_monthly: data.stripe_price_pro_monthly || "",
      stripe_price_pro_yearly: data.stripe_price_pro_yearly || "",
      stripe_price_business_monthly: data.stripe_price_business_monthly || "",
      stripe_price_business_yearly: data.stripe_price_business_yearly || "",
      stripe_price_enterprise_monthly: data.stripe_price_enterprise_monthly || "",
      stripe_price_enterprise_yearly: data.stripe_price_enterprise_yearly || "",
    }));
  }).catch(() => {});

  const stripeField = (k) => (e) => setStripe({ ...stripe, [k]: e.target.value });

  const saveStripe = async () => {
    setSavingStripe(true);
    try {
      const payload = { ...stripe };
      if (!payload.stripe_billing_webhook_secret) delete payload.stripe_billing_webhook_secret;
      await api.put("/admin/stripe", payload);
      toast.success("Configuration Stripe enregistrée");
      if (stripe.stripe_billing_webhook_secret) {
        setWebhookSecretSet(true);
        setStripe((f) => ({ ...f, stripe_billing_webhook_secret: "" }));
      }
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSavingStripe(false); }
  };

  useEffect(() => { loadLlm(); loadPlatform(); loadStripe(); }, []);

  const fromDomain = pf.smtp_central_from_domain || "acteio.com";

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-3xl space-y-6" data-testid="platform-settings">
      <div className="mb-2">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Administration</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Paramètres plateforme</h1>
        <p className="text-muted-foreground mt-2">Réglages globaux appliqués à toute la plateforme : moteur IA, envoi d'emails de secours, facturation Stripe et options générales.</p>
      </div>

      {/* ═══════════ Moteur IA ═══════════ */}
      <div className="bg-card border border-border rounded-lg p-6 lg:p-8 shadow-sm space-y-5" data-testid="admin-llm-settings">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Cpu className="w-[18px] h-[18px] text-primary" />
            </div>
            <h3 className="font-heading text-lg font-semibold">Moteur IA</h3>
          </div>
          <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold ${llm.llm_provider !== "emergent" && llmKeySet ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-muted text-muted-foreground"}`} data-testid="admin-llm-status">
            {llm.llm_provider === "emergent" ? "Défaut" : (llmKeySet ? "Clé propre" : "Clé requise")}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">Fournisseur d'IA appliqué à toute la plateforme. Une société peut surcharger ce réglage avec sa propre clé dans ses Paramètres.</p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Fournisseur</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="admin-llm-provider">
              {PROVIDERS.map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => setLlm((f) => ({ ...f, llm_provider: v }))}
                  data-testid={`admin-llm-provider-${v}`}
                  className={`h-10 text-sm font-medium rounded-md border transition-all cursor-pointer ${llm.llm_provider === v ? "bg-primary text-white border-primary shadow-sm" : "bg-transparent text-muted-foreground border-border hover:border-primary/40 hover:text-foreground hover:bg-muted/50"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Modèle</Label>
            <Input value={llm.llm_model} onChange={(e) => setLlm((f) => ({ ...f, llm_model: e.target.value }))}
              data-testid="admin-llm-model" placeholder={PLACEHOLDER[llm.llm_provider]} className="mt-1.5 rounded-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Laissez vide pour le modèle par défaut du fournisseur.</p>
          </div>
        </div>

        {llm.llm_provider !== "emergent" && (
          <div>
            <Label>Clé API {llm.llm_provider}</Label>
            <Input type="password" value={llm.llm_api_key} data-testid="admin-llm-key"
              onChange={(e) => setLlm((f) => ({ ...f, llm_api_key: e.target.value }))}
              placeholder={llmKeySet ? "•••••••• (inchangée)" : "Votre clé API"} className="mt-1.5 rounded-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Stockée de façon sécurisée, jamais renvoyée. En cas d'échec, le fournisseur par défaut prend le relais.</p>
          </div>
        )}

        <div className="pt-1 flex items-center gap-3 flex-wrap">
          <Button onClick={saveLlm} disabled={savingLlm} data-testid="admin-save-llm" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {savingLlm ? "Enregistrement…" : "Enregistrer le moteur IA"}
          </Button>
          <Button onClick={runLlmTest} disabled={testingLlm} variant="outline" data-testid="admin-test-llm" className="rounded-sm font-semibold">
            {testingLlm ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Test en cours…</> : "Tester la connexion IA"}
          </Button>
        </div>
        {llmTest && (
          <div data-testid="admin-llm-test-result" className={`text-sm rounded-sm px-3 py-2.5 border ${llmTest.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
            {llmTest.ok ? (
              <span><Check className="w-4 h-4 inline mr-1.5 -mt-0.5" />Connexion réussie — <strong>{llmTest.provider}</strong> · {llmTest.model} · {llmTest.latency_ms} ms. Réponse : « {llmTest.sample} »</span>
            ) : (<span><strong>Échec :</strong> {llmTest.error}</span>)}
          </div>
        )}
      </div>

      {/* ═══════════ SMTP central (secours plateforme) ═══════════ */}
      <div className="bg-card border border-border rounded-lg p-6 lg:p-8 shadow-sm space-y-5" data-testid="smtp-central-settings">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Mail className="w-[18px] h-[18px] text-primary" />
            </div>
            <h3 className="font-heading text-lg font-semibold">Envoi central (secours plateforme)</h3>
          </div>
          <span className="text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold bg-primary/10 text-primary border border-primary/20" data-testid="central-email-provider-badge">
            {pf.central_email_provider === "postal" ? "API Postal" : "SMTP"}
          </span>
        </div>

        {/* Choix de la méthode d'envoi central */}
        <div>
          <Label>Méthode d'envoi</Label>
          <div className="mt-1.5 flex w-fit rounded-sm border border-border overflow-hidden" data-testid="central-email-provider">
            {[["smtp", "SMTP"], ["postal", "API Postal"]].map(([v, lbl]) => (
              <button key={v} type="button" onClick={() => pfField("central_email_provider", v)}
                data-testid={`central-provider-${v}`}
                className={`px-4 h-9 text-sm font-medium transition-colors ${pf.central_email_provider === v ? "bg-primary text-white" : "bg-transparent text-muted-foreground hover:bg-muted"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {pf.central_email_provider === "smtp" && (<>
        <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer" data-testid="smtp-central-enabled-label">
          <input type="checkbox" checked={pf.smtp_central_enabled} data-testid="smtp-central-enabled"
            onChange={(e) => pfField("smtp_central_enabled", e.target.checked)} className="w-4 h-4 rounded-sm" />
          Activer le SMTP central
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Hôte SMTP</Label>
            <Input value={pf.smtp_central_host} onChange={(e) => pfField("smtp_central_host", e.target.value)}
              data-testid="smtp-central-host" placeholder="smtp.example.com" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Port</Label>
            <Input type="number" value={pf.smtp_central_port} onChange={(e) => pfField("smtp_central_port", e.target.value)}
              data-testid="smtp-central-port" placeholder="587" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Utilisateur</Label>
            <Input value={pf.smtp_central_user} onChange={(e) => pfField("smtp_central_user", e.target.value)}
              data-testid="smtp-central-user" placeholder="apikey / user@example.com" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Mot de passe</Label>
            <Input type="password" value={pf.smtp_central_password} onChange={(e) => pfField("smtp_central_password", e.target.value)}
              data-testid="smtp-central-password" placeholder={smtpPwSet ? "•••••• (inchangé)" : "Mot de passe SMTP"} className="mt-1.5 rounded-sm" />
          </div>
          <div className="sm:col-span-2">
            <Label>Domaine expéditeur</Label>
            <Input value={pf.smtp_central_from_domain} onChange={(e) => pfField("smtp_central_from_domain", e.target.value)}
              data-testid="smtp-central-from-domain" placeholder="acteio.com" className="mt-1.5 rounded-sm" />
          </div>
        </div>

        <div className="text-sm rounded-sm px-3 py-2.5 border bg-muted/40 border-border text-muted-foreground" data-testid="smtp-central-help">
          Les sociétés sans SMTP enverront via ce serveur, avec l'adresse <code className="font-mono text-xs bg-muted px-1 rounded">{"{slug}"}.proposition@{fromDomain}</code> (nom affiché = nom de la société).
        </div>
        </>)}

        {pf.central_email_provider === "postal" && (<>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="postal-fields">
          <div className="sm:col-span-2">
            <Label>URL du serveur Postal</Label>
            <Input value={pf.postal_url} onChange={(e) => pfField("postal_url", e.target.value)}
              data-testid="postal-url" placeholder="https://postal.votredomaine.com" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Clé API serveur</Label>
            <Input type="password" value={pf.postal_api_key} onChange={(e) => pfField("postal_api_key", e.target.value)}
              data-testid="postal-api-key" placeholder={postalKeySet ? "•••••• (inchangée)" : "X-Server-API-Key"} className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Domaine expéditeur</Label>
            <Input value={pf.postal_from_domain} onChange={(e) => pfField("postal_from_domain", e.target.value)}
              data-testid="postal-from-domain" placeholder="acteio.com" className="mt-1.5 rounded-sm" />
          </div>
          <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
            <div>
              <Label>SPF include (hôte)</Label>
              <Input value={pf.postal_spf_host} onChange={(e) => pfField("postal_spf_host", e.target.value)}
                data-testid="postal-spf-host" placeholder="spf.acteio.com" className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label>Return-path (hôte)</Label>
              <Input value={pf.postal_return_path_host} onChange={(e) => pfField("postal_return_path_host", e.target.value)}
                data-testid="postal-return-path-host" placeholder="rp.acteio.com" className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label>Return-path (préfixe)</Label>
              <Input value={pf.postal_return_path_prefix} onChange={(e) => pfField("postal_return_path_prefix", e.target.value)}
                data-testid="postal-return-path-prefix" placeholder="psrp" className="mt-1.5 rounded-sm" />
            </div>
          </div>
        </div>
        <div className="text-sm rounded-sm px-3 py-2.5 border bg-muted/40 border-border text-muted-foreground" data-testid="postal-help">
          Envoi via l'API HTTP Postal (<code className="font-mono text-xs bg-muted px-1 rounded">POST {"{url}"}/api/v1/send/message</code>). Les sociétés sans SMTP enverront avec l'adresse <code className="font-mono text-xs bg-muted px-1 rounded">{"{slug}"}.proposition@{pf.postal_from_domain || "acteio.com"}</code>. La clé API est une <strong>clé serveur</strong> Postal (Credentials → API).
        </div>
        </>)}

        <div className="pt-1">
          <Button onClick={saveSmtp} disabled={savingSmtp} data-testid="smtp-central-save" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {savingSmtp ? "Enregistrement…" : "Enregistrer l'envoi central"}
          </Button>
        </div>

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-semibold">Tester l'envoi</h4>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label>Envoyer un test à</Label>
              <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                data-testid="smtp-central-test-to" placeholder="vous@example.com" className="mt-1.5 rounded-sm" />
            </div>
            <Button onClick={runSmtpTest} disabled={smtpTesting} variant="outline" data-testid="smtp-central-test" className="rounded-sm font-semibold">
              {smtpTesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi…</> : <><Send className="w-4 h-4 mr-2" />Tester l'envoi</>}
            </Button>
          </div>
          {smtpTest && (
            <div data-testid="smtp-central-test-result" className={`text-sm rounded-sm px-3 py-2.5 border ${smtpTest.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
              {smtpTest.ok ? (
                <span><Check className="w-4 h-4 inline mr-1.5 -mt-0.5" />Email de test envoyé avec succès.</span>
              ) : (<span><strong>Échec :</strong> {smtpTest.error}</span>)}
            </div>
          )}
        </div>

        {/* Prérequis DNS / délivrabilité */}
        <details className="pt-4 border-t border-border group" data-testid="smtp-central-dns">
          <summary className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none list-none">
            <Info className="w-4 h-4 text-primary" />
            Prérequis DNS sur <code className="font-mono text-xs bg-muted px-1 rounded">{fromDomain}</code>
            <span className="text-xs font-normal text-muted-foreground ml-1">(pour ne pas finir en spam)</span>
          </summary>
          <div className="mt-4 space-y-4 text-sm text-muted-foreground">
            <p>
              Pour que les emails envoyés depuis <code className="font-mono text-xs bg-muted px-1 rounded">&lt;slug&gt;.proposition@{fromDomain}</code>{" "}
              (nom affiché = nom de la société) arrivent en boîte de réception, configurez sur le domaine <strong>{fromDomain}</strong> :
            </p>
            <ol className="space-y-3 list-decimal ml-4">
              <li>
                <strong className="text-foreground">Boîte catch-all</strong> — une adresse attrape-tout{" "}
                <code className="font-mono text-xs bg-muted px-1 rounded">*@{fromDomain}</code>{" "}
                (ou au moins <code className="font-mono text-xs bg-muted px-1 rounded">*.proposition@{fromDomain}</code>)
                pour recevoir les réponses des prospects.
              </li>
              <li>
                <strong className="text-foreground">SPF</strong> — un enregistrement <code className="font-mono text-xs bg-muted px-1 rounded">TXT</code> sur{" "}
                <code className="font-mono text-xs bg-muted px-1 rounded">{fromDomain}</code> qui autorise votre serveur d'envoi :
                <pre className="mt-1.5 bg-muted rounded-sm p-2.5 text-xs font-mono text-foreground overflow-x-auto">v=spf1 include:VOTRE-FOURNISSEUR-SMTP ~all</pre>
                (ou <code className="font-mono text-xs bg-muted px-1 rounded">ip4:IP_DU_SERVEUR</code> à la place de l'<code className="font-mono text-xs">include</code>).
              </li>
              <li>
                <strong className="text-foreground">DKIM</strong> — générez une clé DKIM sur votre serveur/fournisseur SMTP, puis publiez le{" "}
                <code className="font-mono text-xs bg-muted px-1 rounded">TXT</code> fourni sur{" "}
                <code className="font-mono text-xs bg-muted px-1 rounded">SELECTEUR._domainkey.{fromDomain}</code>.
              </li>
              <li>
                <strong className="text-foreground">DMARC</strong> — un <code className="font-mono text-xs bg-muted px-1 rounded">TXT</code> sur{" "}
                <code className="font-mono text-xs bg-muted px-1 rounded">_dmarc.{fromDomain}</code> :
                <pre className="mt-1.5 bg-muted rounded-sm p-2.5 text-xs font-mono text-foreground overflow-x-auto">v=DMARC1; p=none; rua=mailto:postmaster@{fromDomain}</pre>
                (démarrez en <code className="font-mono text-xs">p=none</code>, puis durcissez en <code className="font-mono text-xs">quarantine</code> / <code className="font-mono text-xs">reject</code>).
              </li>
              <li>
                <strong className="text-foreground">Vérifiez la délivrabilité</strong> — envoyez un test (bouton ci-dessus) vers un outil comme{" "}
                <a href="https://www.mail-tester.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">mail-tester.com</a>{" "}
                et visez un score de 9–10/10 avant d'activer pour vos clients.
              </li>
            </ol>
            <p className="text-xs">
              Les valeurs SPF/DKIM exactes dépendent de votre fournisseur SMTP (celui saisi ci-dessus). Ces enregistrements se posent chez le registrar / la zone DNS de <strong>{fromDomain}</strong>.
            </p>
          </div>
        </details>
      </div>

      {/* ═══════════ Stripe plateforme ═══════════ */}
      <div className="bg-card border border-border rounded-lg p-6 lg:p-8 shadow-sm space-y-6" data-testid="stripe-platform-settings">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <CreditCard className="w-[18px] h-[18px] text-primary" />
          </div>
          <h3 className="font-heading text-lg font-semibold">Stripe plateforme</h3>
        </div>

        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm font-semibold border ${
            stripeKeySet ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground border-transparent"
          }`} data-testid="stripe-key-status">
            {stripeKeySet ? <><Check className="w-3 h-3" /> Configurée</> : "Non configurée"}
          </span>
          <p className="text-sm text-muted-foreground">
            La clé <code className="font-mono text-xs bg-muted px-1 rounded">STRIPE_API_KEY</code> est définie dans le fichier <code className="font-mono text-xs bg-muted px-1 rounded">.env</code> du serveur.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold mb-1">Price IDs Stripe par forfait</h4>
            <p className="text-sm text-muted-foreground">Créez les produits dans votre dashboard Stripe puis collez les IDs ici (<code className="font-mono text-xs">price_xxx</code>).</p>
          </div>
          {["pro", "business", "enterprise"].map((plan) => (
            <div key={plan} className="space-y-3">
              <h4 className="text-sm font-semibold capitalize border-b border-border pb-2">{plan}</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Mensuel</Label>
                  <Input value={stripe[`stripe_price_${plan}_monthly`]} onChange={stripeField(`stripe_price_${plan}_monthly`)}
                    data-testid={`stripe-price-${plan}-monthly`} placeholder="price_xxx" className="mt-1.5 rounded-sm font-mono text-xs" />
                </div>
                <div>
                  <Label>Annuel</Label>
                  <Input value={stripe[`stripe_price_${plan}_yearly`]} onChange={stripeField(`stripe_price_${plan}_yearly`)}
                    data-testid={`stripe-price-${plan}-yearly`} placeholder="price_xxx" className="mt-1.5 rounded-sm font-mono text-xs" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-semibold">Secret webhook abonnements</h4>
          <p className="text-sm text-muted-foreground">
            URL du webhook à renseigner dans Stripe : <code className="font-mono text-xs bg-muted px-1 rounded">https://app.acteio.com/api/companies/webhook/billing</code><br />
            Événements à activer : <code className="font-mono text-xs">customer.subscription.*</code>, <code className="font-mono text-xs">invoice.payment_failed</code>
          </p>
          <div>
            <Label>
              Signing secret (whsec_…)
              {webhookSecretSet && <span className="ml-2 text-emerald-600 text-xs font-normal">● Configuré</span>}
            </Label>
            <Input type="password" value={stripe.stripe_billing_webhook_secret} onChange={stripeField("stripe_billing_webhook_secret")}
              data-testid="stripe-webhook-secret" placeholder={webhookSecretSet ? "Laisser vide pour conserver l'actuel" : "whsec_xxx"} className="mt-1.5 rounded-sm" />
          </div>
        </div>

        <Button onClick={saveStripe} disabled={savingStripe} data-testid="stripe-save" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
          {savingStripe ? "Enregistrement…" : "Enregistrer la configuration Stripe"}
        </Button>
      </div>

      {/* ═══════════ Général ═══════════ */}
      <div className="bg-card border border-border rounded-lg p-6 lg:p-8 shadow-sm space-y-5" data-testid="general-settings">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="w-[18px] h-[18px] text-primary" />
          </div>
          <h3 className="font-heading text-lg font-semibold">Général</h3>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>URL publique de l'application</Label>
            <Input value={pf.platform_app_url} onChange={(e) => pfField("platform_app_url", e.target.value)}
              data-testid="platform-app-url" placeholder="https://app.acteio.com" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Email de support</Label>
            <Input type="email" value={pf.platform_support_email} onChange={(e) => pfField("platform_support_email", e.target.value)}
              data-testid="platform-support-email" placeholder="support@acteio.com" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label>Jours d'essai</Label>
            <Input type="number" min="0" value={pf.platform_trial_days} onChange={(e) => pfField("platform_trial_days", e.target.value)}
              data-testid="platform-trial-days" placeholder="14" className="mt-1.5 rounded-sm" />
          </div>
          <div className="sm:col-span-2">
            <Label>
              Webhook Slack
              {slackSet && <span className="ml-2 text-emerald-600 text-xs font-normal">● Configuré</span>}
            </Label>
            <Input type="password" value={pf.platform_slack_webhook} onChange={(e) => pfField("platform_slack_webhook", e.target.value)}
              data-testid="platform-slack-webhook" placeholder={slackSet ? "•••••• (inchangé)" : "https://hooks.slack.com/services/…"} className="mt-1.5 rounded-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Stocké de façon sécurisée, jamais renvoyé.</p>
          </div>
        </div>

        <div className="pt-1">
          <Button onClick={saveGeneral} disabled={savingGeneral} data-testid="general-save" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {savingGeneral ? "Enregistrement…" : "Enregistrer les paramètres généraux"}
          </Button>
        </div>
      </div>
    </div>
  );
}
